import axios from "axios";
import { deepmerge } from "deepmerge-ts";
import FileValidatorConfig from "features/validation/models/FileValidatorConfig";
import { TFunction } from "i18next";
import { BackendOptions, RequestCallback } from "i18next-http-backend";
import IDataFilterDescriptor from "features/shared/components/dataFilter/IDataFilterDescriptor";
import FormFieldDescriptor from "features/shared/forms/dynamicForm/models/FormFieldDescriptor";
import { loadModule } from "./modules";
import { getPortalBasename } from "./urlHelper";

const fileValidatorConfig = loadModule<FileValidatorConfig>(
  "validators/fileValidator.json"
);

export const getSingleFileValidationMessage = (t: TFunction) => {
  let fileSizeMsg = t("fileInputSingleFile", {
    fileSize: fileValidatorConfig.maxFileSizeInMb,
  });

  return fileSizeMsg;
};

export const getMultipleFilesValidationMessage = (t: TFunction) => {
  let filesMsg = t("fileInputMultipleFiles", {
    filesCount: fileValidatorConfig.maxFilesCount,
    fileSize: fileValidatorConfig.maxFileSizeInMb,
    totalFilesSize: fileValidatorConfig.maxFilesSizeInMb
  });

  return filesMsg;
};

export const getFileExtensionValidationMessage = (t: TFunction) => {
  return t("fileInputEnabledExtensions", {
    extensions: fileValidatorConfig.enabledExtensions.join(", "),
  });
};

export const getFullFileValidationMessage = (t: TFunction, multipleFiles: boolean = false) => {
  if (multipleFiles) {
    return `${getMultipleFilesValidationMessage(t)} ${getFileExtensionValidationMessage(t)}`;
  } else {
    return `${getSingleFileValidationMessage(t)} ${getFileExtensionValidationMessage(t)}`;
  }
}

export const getPluralizedMessage = (t: TFunction, key: string, count: number) => {
  let countToTranslate = count < 5 ? count : 5;
  return t(`${key}_${countToTranslate}`)
}

export const fetchTranslation = async (url: string): Promise<any> => {
 
  try {
    let translation = await axios.get(url);
    return translation.data;
  } catch (e) { 
    return null;
  }
}

export const translationRequest = async (
  options: BackendOptions,
  url: string,
  payload: string | {},
  callback: RequestCallback
): Promise<void> => {
  const customTranslation = await fetchTranslation(`${getPortalBasename()}/configuration/${process.env.REACT_APP_CLIENT}${url}`);
  const defaultTranslation = await fetchTranslation(`${getPortalBasename()}${url}`);

  let error = null;
  let response: any = undefined;

  if (defaultTranslation === null) {
    error = new Error(`Default translation not be loaded. Path: ${url}`);
    response = null;
  } else {
    let resultJson = {};

    if (customTranslation !== null && typeof customTranslation === "object") {
      resultJson = deepmerge(defaultTranslation, customTranslation);
    } else {
      resultJson = defaultTranslation;
    }
    
    response = {
      status: 200,
      data: JSON.stringify(resultJson),
    }
  } 

  callback(error, response!);
};

export const getTranslatedFormText = (
  descriptor: FormFieldDescriptor | IDataFilterDescriptor,
  fieldName: "label" | "placeholder",
  translate: TFunction
): string => {
  let label = (descriptor as any)[fieldName];
  if (descriptor.translationPrefix) {
    let translationCode = `${descriptor.translationPrefix}.${descriptor.name}.${fieldName}`;
    let translation = translate(translationCode);
    label = translation === translationCode ? label : translation;
  }

  return label;
};